<script lang="ts">
  import { SlidersHorizontal, Search, Map, X } from "lucide-svelte";
  import { Input } from "$lib/components/ui/input";
  import { Button } from "$lib/components/ui/button";
  import { Badge } from "$lib/components/ui/badge";
  import { searchStore } from "$lib/stores/search.svelte";
  import { stateStore } from "$lib/stores/state.svelte";
  import Categories from "./categories.svelte";
  import { filtersStore } from "$lib/stores/filters.svelte";

  let searchValue = $state("");
  let hasSearchValue = $derived(!!searchValue.trim());
  let searchTimeout: NodeJS.Timeout;

  let userLat = $state<number | undefined>(47.497912);
  let userLng = $state<number | undefined>(19.040235);

  // Search effect with debouncing
  $effect(() => {
    if (!!searchValue.trim()) {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        handleSearch();
      }, 300);
    } else {
      searchStore.clearResults();
    }
  });

  // Cleanup effect
  $effect(() => {
    return () => clearTimeout(searchTimeout);
  });

  async function handleSearch() {
    if (!hasSearchValue) return;
    stateStore.openSearch();
    await searchStore.search(searchValue.trim(), userLat, userLng);
  }

  let isFocused = $state(false);

  function handleCategoryClick() {
    stateStore.toggleCategory();
  }

  function handleFilterClick() {
    stateStore.toggleFilter();
  }

  function handleFocus() {
    isFocused = true;
    if (hasSearchValue) {
      stateStore.openSearch();
    }
  }

  function handleBlur() {
    setTimeout(() => {
      if (stateStore.activePage || hasSearchValue) return;
      isFocused = false;
    }, 1000);
  }

  $effect(() => {
    if (!stateStore.activePage) {
      isFocused = false;
    }
  });

  let placeHolder = $derived.by(() => {
    if (filtersStore.activeCategory) {
      return filtersStore.activeCategory.label;
    }

    if (filtersStore.activeFilter) {
      return "Ökos pont keresése (szűrt)";
    }

    return "Ökos pont keresése";
  });

  let canDelete = $derived.by(() => {
    return (
      !!searchValue.trim() ||
      filtersStore.activeCategory ||
      filtersStore.activeFilter
    );
  });

  const handleCleanSearch = () => {
    filtersStore.toggleAllFiltersAndSubcategories();
    searchValue = "";
  };
</script>

<div class="flex gap-1 px-2">
  <div
    class="relative flex-1 transition-all duration-300
        {isFocused || stateStore.sidebaropen
      ? 'mx-auto w-fit sm:mx-0 sm:w-auto'
      : 'translate-x-1/3 translate-y-12 sm:translate-x-16 sm:translate-y-0'}"
  >
    <Search
      strokeWidth={3}
      class="absolute left-3 top-1/2 z-10 -translate-y-1/2 h-5 w-5 
          text-muted-foreground transition-all duration-300
          {isFocused ? '-translate-x-0.5' : ''}"
    />

    <Input
      bind:value={searchValue}
      placeholder={placeHolder}
      class="pl-10 h-12 sm:h-14 border-foreground text-sm sm:text-base backdrop-blur-md selection:bg-background
    hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px] hover:shadow-foreground transition-all duration-300
    {isFocused || stateStore.sidebaropen
        ? 'w-full bg-card font-extrabold'
        : 'w-56 sm:w-64 bg-secondary/30 font-bold'}
    {stateStore.sidebaropen
        ? 'focus-visible:bg-primary'
        : 'focus-visible:bg-hl-yellow/30'}"
      onfocus={handleFocus}
      onblur={handleBlur}
    />
    <button
      hidden={!canDelete}
      onclick={handleCleanSearch}
      class="absolute right-3 top-1/2 -translate-y-1/2 {isFocused ||
      stateStore.sidebaropen
        ? 'opacity-100'
        : 'opacity-0 pointer-events-none'}"
    >
      <X />
    </button>

    {#if searchStore.isLoading && hasSearchValue}
      <div class="absolute right-3 top-1/2 -translate-y-1/2">
        <div
          class="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"
        ></div>
      </div>
    {/if}
  </div>

  <!-- Action Buttons -->
  <div
    class="flex gap-2 transition-all duration-300
        {isFocused || stateStore.sidebaropen
      ? 'opacity-100 translate-x-0'
      : 'opacity-0 -translate-x-2 pointer-events-none'}"
  >
    <Button
      variant="outline"
      size="lg"
      onclick={handleFilterClick}
      class="bg-card
    {filtersStore.activeCategory
        ? 'bg-primary text-primary-foreground hover:bg-primary'
        : 'hover:bg-hl-green hover:text-foreground'}"
    >
      <Map class="!h-6 !w-6" />
    </Button>

    <Button
      variant="outline"
      size="lg"
      onclick={handleCategoryClick}
      class="bg-card
    {filtersStore.activeFilter && !filtersStore.activeCategory
        ? 'bg-primary text-primary-foreground hover:bg-primary border-foreground'
        : 'hover:bg-hl-green hover:text-foreground'}"
    >
      <SlidersHorizontal class="!h-6 !w-6" />
    </Button>
  </div>
</div>

<!-- Category Badges -->
<div
  class="transition-all duration-300 delay-100 px-2
      {isFocused || stateStore.sidebaropen
    ? 'opacity-100 translate-y-0'
    : 'opacity-0 -translate-y-2 pointer-events-none'}"
>
  {#if stateStore.activePage !== "category" && stateStore.activePage !== "filter"}
    <Categories isFull={false} />
  {/if}
</div>
